import React, { useState, useMemo } from 'react';
import { ChevronDown, ChevronUp, BarChart2, Calendar, CheckCircle, Clock, AlertTriangle, Check } from 'react-feather';
import { FeatureTag } from '../ui/feature-tag';
import './FeatureTagStats.css';

const FeatureTagStats = ({ featureTags, tasks, defaultExpanded = false, date, taskDistribution = [], isCollapsed: controlledCollapsed, onToggleCollapse }) => {
    const [activeType, setActiveType] = useState('type1'); // 'type1', 'type2', or 'type3'
    const [internalCollapsed, setInternalCollapsed] = useState(!defaultExpanded);
    const isCollapsed = controlledCollapsed !== undefined ? controlledCollapsed : internalCollapsed;
    const setIsCollapsed = onToggleCollapse 
        ? (_value) => onToggleCollapse() 
        : (value) => setInternalCollapsed(value);
    const [expandedCompletedFeatures, setExpandedCompletedFeatures] = useState(new Set());

    // Calculate stats for each feature tag
    const featureStats = useMemo(() => {
        return featureTags.map(tag => {
            const tagTasks = tasks.filter(task => task.feature_tag?.id === tag.id);
            const totalTasks = tagTasks.length;
            const completedTasks = tagTasks.filter(task => 
                ['completed', 'released'].includes(task.status)
            );
            const pendingTasks = tagTasks.filter(task => 
                ['open', 'in-progress'].includes(task.status)
            );
            
            // Calculate completion percentage
            const completionPercentage = totalTasks > 0 
                ? Math.round((completedTasks.length / totalTasks) * 100) 
                : 0;
            
            // Find most recent updated_at from any task (regardless of status)
            const lastTaskUpdate = tagTasks.length > 0
                ? tagTasks.reduce((latest, task) => {
                    if (!task.updated_at) return latest;
                    const taskDate = new Date(task.updated_at);
                    if (isNaN(taskDate.getTime())) return latest;
                    if (!latest) return taskDate;
                    return taskDate.getTime() > latest.getTime() ? taskDate : latest;
                }, null)
                : null;

            const allTasksCompleted = totalTasks > 0 && completedTasks.length === totalTasks;

            // Calculate delay days for completed features with due dates
            let delayDays = null;
            if (allTasksCompleted && tag.due_date && lastTaskUpdate) {
                const dueDate = new Date(tag.due_date);
                const completionDate = new Date(lastTaskUpdate);
                const delayTime = completionDate - dueDate;
                const delayDaysValue = Math.ceil(delayTime / (1000 * 60 * 60 * 24));
                // Only set delay if completion was after due date
                if (delayDaysValue > 0) {
                    delayDays = delayDaysValue;
                }
            }

            // Calculate in-progress tasks for the selected date (from work plans)
            const inProgressToday = taskDistribution
                ?.filter(task => 
                    task.status === 'in-progress' && 
                    task.feature_tag?.id === tag.id
                ).length || 0;

            // Calculate due date status
            let dueDateStatus = null;
            if (tag.due_date) {
                const dueDate = new Date(tag.due_date);
                const now = new Date();
                const diffTime = dueDate - now;
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                
                if (diffDays < 0) {
                    dueDateStatus = 'overdue';
                } else if (diffDays === 0) {
                    dueDateStatus = 'due-today';
                } else if (diffDays <= 3) {
                    dueDateStatus = 'urgent';
                } else if (diffDays <= 7) {
                    dueDateStatus = 'upcoming';
                } else {
                    dueDateStatus = 'normal';
                }
            }

            return {
                ...tag,
                totalTasks,
                completedTasks: completedTasks.length,
                pendingTasks: pendingTasks.length,
                inProgressToday,
                completionPercentage,
                lastTaskUpdate,
                allTasksCompleted,
                dueDateStatus,
                delayDays
            };
        });
    }, [featureTags, tasks, taskDistribution]);

    // Group features by type
    const groupedFeatures = useMemo(() => {
        const type1 = featureStats.filter(f => f.due_date !== null && f.due_date !== undefined);
        const type2 = featureStats.filter(f => f.allTasksCompleted);
        const type3 = featureStats.filter(f => !f.due_date || f.due_date === null || f.due_date === undefined);

        return { type1, type2, type3 };
    }, [featureStats]);

    const getActiveFeatures = () => {
        let features = [];
        switch (activeType) {
            case 'type1': features = groupedFeatures.type1; break;
            case 'type2': features = groupedFeatures.type2; break;
            case 'type3': features = groupedFeatures.type3; break;
            default: return [];
        }
        
        // Sort features: 100% completion last, others by completion percentage
        return features.sort((a, b) => {
            if (a.completionPercentage === 100 && b.completionPercentage !== 100) return 1;
            if (b.completionPercentage === 100 && a.completionPercentage !== 100) return -1;
            return a.completionPercentage - b.completionPercentage;
        });
    };

    const formatDate = (date) => {
        if (!date) return 'N/A';
        const dateObj = date instanceof Date ? date : new Date(date);
        const now = new Date();
        const diffTime = now - dateObj;
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        
        if (diffDays === 0) return 'Today';
        if (diffDays === 1) return '1 day ago';
        if (diffDays < 7) return `${diffDays} days ago`;
        if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
        
        return dateObj.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: dateObj.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
        });
    };

    const formatDueDate = (dueDate) => {
        if (!dueDate) return 'N/A';
        const date = new Date(dueDate);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const getDueDateInfo = (feature) => {
        if (!feature.due_date) return null;
        const date = new Date(feature.due_date);
        const now = new Date();
        const diffTime = date - now;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        if (diffDays < 0) {
            return { text: `${Math.abs(diffDays)}d overdue`, status: 'overdue' };
        } else if (diffDays === 0) {
            return { text: 'Due today', status: 'due-today' };
        } else if (diffDays <= 3) {
            return { text: `${diffDays}d left`, status: 'urgent' };
        } else if (diffDays <= 7) {
            return { text: `${diffDays}d left`, status: 'upcoming' };
        } else {
            return { text: formatDueDate(feature.due_date), status: 'normal' };
        }
    };

    const activeFeatures = getActiveFeatures();

    return (
        <div className="feature-tag-stats-section">
            {controlledCollapsed === undefined && (
                <div 
                    className="feature-tag-stats-header" 
                    onClick={() => setIsCollapsed(!isCollapsed)} 
                    style={{ cursor: 'pointer' }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1 }}>
                        <h3 className="feature-tag-stats-title">
                            <BarChart2 size={16} />
                            Feature Tag Stats
                        </h3>
                    </div>
                    <button 
                        className="stats-toggle-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsCollapsed(!isCollapsed);
                        }}
                        aria-label={isCollapsed ? "Expand stats" : "Collapse stats"}
                    >
                        {isCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                    </button>
                </div>
            )}
            
            <div className={`feature-tag-stats-content ${isCollapsed ? 'collapsed' : ''}`}>
                {/* Type Toggle Buttons */}
                <div className="feature-stats-type-toggles">
                    <button
                        className={`feature-stats-type-toggle ${activeType === 'type1' ? 'active' : ''}`}
                        onClick={() => setActiveType('type1')}
                    >
                        <Calendar size={14} />
                        <span>With Due Dates</span>
                        <span className="feature-stats-count">{groupedFeatures.type1.length}</span>
                    </button>
                    <button
                        className={`feature-stats-type-toggle ${activeType === 'type2' ? 'active' : ''}`}
                        onClick={() => setActiveType('type2')}
                    >
                        <CheckCircle size={14} />
                        <span>All Completed</span>
                        <span className="feature-stats-count">{groupedFeatures.type2.length}</span>
                    </button>
                    <button
                        className={`feature-stats-type-toggle ${activeType === 'type3' ? 'active' : ''}`}
                        onClick={() => setActiveType('type3')}
                    >
                        <BarChart2 size={14} />
                        <span>No Due Date</span>
                        <span className="feature-stats-count">{groupedFeatures.type3.length}</span>
                    </button>
                </div>

                {/* Feature Stats Grid */}
                {activeFeatures.length === 0 ? (
                    <div className="feature-stats-empty">
                        <p>No features match this category</p>
                    </div>
                ) : (
                    <div className="feature-stats-grid">
                        {activeFeatures.map(feature => {
                            const dueDateInfo = getDueDateInfo(feature);
                            const isCompleted = feature.completionPercentage === 100;
                            const isExpanded = expandedCompletedFeatures.has(feature.id);
                            
                            // For completed features, show compact badge by default
                            if (isCompleted && !isExpanded) {
                                return (
                                    <div key={feature.id} className="feature-stat-card-compact feature-stat-card--completed-compact">
                                        <div className="feature-stat-card-compact-content">
                                            <FeatureTag 
                                                name={feature.name} 
                                                color={feature.color}
                                                size="small"
                                            />
                                            <span className="feature-completed-badge feature-completed-badge--compact">
                                                <Check size={12} />
                                                <span>Completed</span>
                                            </span>
                                            {feature.due_date && feature.delayDays && activeType === 'type1' && (
                                                <span className="feature-delay-info feature-delay-info--compact">
                                                    {feature.delayDays}d delay
                                                </span>
                                            )}
                                            <button
                                                className="feature-expand-toggle"
                                                onClick={() => {
                                                    setExpandedCompletedFeatures(prev => {
                                                        const newSet = new Set(prev);
                                                        newSet.add(feature.id);
                                                        return newSet;
                                                    });
                                                }}
                                                title="View full stats"
                                            >
                                                <ChevronDown size={14} />
                                            </button>
                                        </div>
                                    </div>
                                );
                            }
                            
                            // Full card view (for non-completed or expanded completed features)
                            return (
                                <div key={feature.id} className={`feature-stat-card ${isCompleted ? 'feature-stat-card--completed' : ''}`}>
                                    <div className="feature-stat-card-header">
                                        <FeatureTag 
                                            name={feature.name} 
                                            color={feature.color}
                                            size="small"
                                        />
                                        {isCompleted ? (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'flex-end' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                    <span className="feature-completed-badge">
                                                        <Check size={14} />
                                                        <span>Completed</span>
                                                    </span>
                                                    <button
                                                        className="feature-collapse-toggle"
                                                        onClick={() => {
                                                            setExpandedCompletedFeatures(prev => {
                                                                const newSet = new Set(prev);
                                                                newSet.delete(feature.id);
                                                                return newSet;
                                                            });
                                                        }}
                                                        title="Collapse to badge"
                                                    >
                                                        <ChevronUp size={14} />
                                                    </button>
                                                </div>
                                                {feature.due_date && feature.delayDays && activeType === 'type1' && (
                                                    <span className="feature-delay-info">
                                                        Delayed by {feature.delayDays} day{feature.delayDays !== 1 ? 's' : ''}
                                                    </span>
                                                )}
                                            </div>
                                        ) : (
                                            dueDateInfo && (
                                                <span className={`feature-due-date-badge feature-due-date-badge--${dueDateInfo.status}`}>
                                                    {dueDateInfo.status === 'overdue' && <AlertTriangle size={12} />}
                                                    {dueDateInfo.status === 'due-today' && <Clock size={12} />}
                                                    {dueDateInfo.status === 'urgent' && <Clock size={12} />}
                                                    {dueDateInfo.status === 'upcoming' && <Calendar size={12} />}
                                                    {dueDateInfo.status === 'normal' && <Calendar size={12} />}
                                                    <span>{dueDateInfo.text}</span>
                                                </span>
                                            )
                                        )}
                                    </div>

                                    <div className="feature-stat-card-body">
                                        {/* Progress Bar */}
                                        <div className="feature-stat-progress">
                                            <div className="feature-stat-progress-bar">
                                                <div 
                                                    className="feature-stat-progress-fill"
                                                    style={{ width: `${feature.completionPercentage}%` }}
                                                />
                                            </div>
                                            <span className="feature-stat-progress-text">
                                                {feature.completionPercentage}%
                                            </span>
                                        </div>

                                        {/* Statistics Grid */}
                                        <div className="feature-stat-numbers">
                                            <div className="feature-stat-number-item">
                                                <div className="feature-stat-number-value">{feature.totalTasks}</div>
                                                <div className="feature-stat-number-label">Total</div>
                                            </div>
                                            <div className="feature-stat-number-item feature-stat-number-item--success">
                                                <div className="feature-stat-number-value">{feature.completedTasks}</div>
                                                <div className="feature-stat-number-label">Completed</div>
                                            </div>
                                            {activeType !== 'type2' && (
                                                <div className="feature-stat-number-item feature-stat-number-item--warning">
                                                    <div className="feature-stat-number-value">{feature.pendingTasks}</div>
                                                    <div className="feature-stat-number-label">Pending</div>
                                                </div>
                                            )}
                                            {taskDistribution && taskDistribution.length > 0 && (
                                                <div className="feature-stat-number-item feature-stat-number-item--info">
                                                    <div className="feature-stat-number-value">{feature.inProgressToday}</div>
                                                    <div className="feature-stat-number-label">In Progress Today</div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Last Update */}
                                        <div className="feature-stat-footer">
                                            <Clock size={12} />
                                            <span>Last update: {formatDate(feature.lastTaskUpdate)}</span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default FeatureTagStats;
